import api from './api';
import { User } from './auth';

export interface CreateUserRequest {
  username: string;
  display_name?: string;
  email: string;
  password: string;
  phone?: string;
  client_type?: string;
  customer_categories?: string[];
  contact_name?: string;
  channel?: string;
  source?: string;
  group_number?: string;
  parent_id?: number | null;
  roles?: string[];
  permissions?: string[];
}

export interface UpdateUserRequest {
  username?: string;
  display_name?: string;
  email?: string;
  password?: string;
  parent_id?: number | null;
  roles?: string[];
  permissions?: string[];
}

export interface UpdateProfileRequest {
  display_name?: string;
  email?: string;
  phone: string;
  client_type?: string;
  customer_categories?: string[];
  contact_name?: string;
  channel?: string;
  source?: string;
  group_number?: string;
  parent_id?: number | null;
  roles?: string[];
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: any;
}

export interface UserListParams {
  page?: number;
  per_page?: number;
  search?: string;
  role?: string;
  parent_id?: number | 'null';
}

export interface PaginatedUserResponse {
  success: boolean;
  data: {
    data: User[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  filter_options?: {
    parents?: Array<Pick<User, 'id' | 'username' | 'display_name' | 'email'>>;
  };
}

class UserService {
  // Get all users
  async getUsers(): Promise<ApiResponse<User[]>> {
    const response = await api.get('/users');
    return response.data;
  }

  // Get paginated users for management page
  async getUsersPage(params: UserListParams): Promise<PaginatedUserResponse> {
    const response = await api.get('/users', { params });
    return response.data;
  }

  // Export users with current management filters
  async exportUsers(params?: Omit<UserListParams, 'page' | 'per_page'>): Promise<Blob> {
    const response = await api.get('/users-export', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    return response.data;
  }

  // Get user by ID
  async getUser(id: number): Promise<ApiResponse<User>> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  }

  // Create user
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    const response = await api.post('/users', userData);
    return response.data;
  }

  // Update user
  async updateUser(id: number, userData: UpdateUserRequest): Promise<ApiResponse<User>> {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  }

  // Delete user
  async deleteUser(id: number): Promise<ApiResponse<null>> {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }

  // Batch delete users
  async batchDeleteUsers(ids: number[]): Promise<ApiResponse<{ deleted_count: number }>> {
    const response = await api.post('/users/batch-delete', { ids });
    return response.data;
  }

  // Assign roles to user
  async assignRoles(id: number, roles: string[]): Promise<ApiResponse<User>> {
    const response = await api.post(`/users/${id}/assign-roles`, { roles });
    return response.data;
  }

  // Assign permissions to user
  async assignPermissions(id: number, permissions: string[]): Promise<ApiResponse<User>> {
    const response = await api.post(`/users/${id}/assign-permissions`, { permissions });
    return response.data;
  }

  // Get current user profile (with meta data)
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/profile');
    return response.data.data;
  }

  // Update current user profile
  async updateProfile(profileData: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await api.put('/profile', profileData);
    return response.data;
  }

  // Update other user profile (admin only)
  async updateUserProfile(userId: number, profileData: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await api.put(`/users/${userId}/profile`, profileData);
    return response.data;
  }

  // Change current user password
  async changePassword(passwordData: ChangePasswordRequest): Promise<ApiResponse<null>> {
    const response = await api.put('/profile/password', passwordData);
    return response.data;
  }
}

const userService = new UserService();
export default userService;
